import React, { useState, useEffect } from 'react';
import { reportsAPI, carAPI, userAPI } from '../../services/api';
import { useSettings } from '../../context/SettingsContext';
import { useAuth } from '../../context/AuthContext';
import PageHeader from '../../components/layout/PageHeader';
import Button from '../../components/ui/Button';
import RefreshButton from '../../components/ui/RefreshButton';
import CarLoader from '../../components/ui/CarLoader';
import { ToastModal } from '../../components/ui/Toast';
import { formatName, formatVehicleName } from '../../lib/utils';
import {
  FileText,
  Download,
  IndianRupee,
  Car,
  Users,
  ClipboardCheck,
  TrendingUp,
  Building2
} from 'lucide-react';

const Reports = () => {
  const { getDefaultDateRange } = useSettings();
  const { user } = useAuth();
  const defaultDates = getDefaultDateRange();
  const isAdmin = user?.role === 'admin';

  const [activeReport, setActiveReport] = useState(isAdmin ? 'by-owner' : 'bookings');
  const [reportData, setReportData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [cars, setCars] = useState([]);
  const [owners, setOwners] = useState([]);
  const [toast, setToast] = useState({ isOpen: false, title: '', message: '', type: 'success' });

  const [filters, setFilters] = useState({
    startDate: defaultDates.start,
    endDate: defaultDates.end,
    status: '',
    carId: '',
    groupBy: 'day',
    ownerId: ''
  });

  useEffect(() => {
    fetchCars();
    if (isAdmin) fetchOwners();
  }, []);

  useEffect(() => {
    if (activeReport) {
      fetchReportData();
    }
  }, [activeReport]);

  const fetchCars = async () => {
    try {
      const response = await carAPI.getAllCars();
      setCars(response.data.cars || []);
    } catch (error) {
      console.error('Error fetching cars:', error);
    }
  };

  const fetchOwners = async () => {
    try {
      const response = await userAPI.getOwnersStats();
      setOwners(response.data.owners || []);
    } catch (error) {
      console.error('Error fetching owners:', error);
    }
  };

  const fetchReportData = async () => {
    setLoading(true);
    try {
      let response;
      const params = {};

      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;
      if (filters.carId) params.carId = filters.carId;
      if (filters.groupBy && activeReport === 'revenue') params.groupBy = filters.groupBy;
      if (isAdmin && filters.ownerId) params.ownerId = filters.ownerId;

      switch (activeReport) {
        case 'by-owner':
          response = await reportsAPI.getOwnerComparisonReport(params);
          break;
        case 'bookings':
          response = await reportsAPI.getBookingsReport(params);
          break;
        case 'revenue':
          response = await reportsAPI.getRevenueReport(params);
          break;
        case 'car-utilization':
          response = await reportsAPI.getCarUtilizationReport(params);
          break;
        case 'customers':
          response = await reportsAPI.getCustomerReport(params);
          break;
        case 'inspections':
          response = await reportsAPI.getInspectionReport(params);
          break;
        default:
          return;
      }

      setReportData(response.data);
    } catch (error) {
      console.error('Error fetching report:', error);
      setToast({
        isOpen: true,
        title: 'Error',
        message: 'Failed to fetch report data. Please try again.',
        type: 'error'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleDownloadCSV = async () => {
    try {
      const params = {};
      if (filters.startDate) params.startDate = filters.startDate;
      if (filters.endDate) params.endDate = filters.endDate;
      if (filters.status) params.status = filters.status;
      if (filters.carId) params.carId = filters.carId;
      if (filters.groupBy && activeReport === 'revenue') params.groupBy = filters.groupBy;

      let response;
      switch (activeReport) {
        case 'bookings':
          response = await reportsAPI.downloadBookingsCSV(params);
          break;
        case 'revenue':
          response = await reportsAPI.downloadRevenueCSV(params);
          break;
        case 'car-utilization':
          response = await reportsAPI.downloadCarUtilizationCSV(params);
          break;
        case 'customers':
          response = await reportsAPI.downloadCustomerCSV(params);
          break;
        case 'inspections':
          response = await reportsAPI.downloadInspectionCSV(params);
          break;
        default:
          return;
      }

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${activeReport}-report-${Date.now()}.csv`);
      document.body.appendChild(link);
      link.click();
      link.remove();

      setToast({
        isOpen: true,
        title: 'Download Started',
        message: 'Your CSV report is being downloaded.',
        type: 'success'
      });
    } catch (error) {
      console.error('Error downloading CSV:', error);
      setToast({
        isOpen: true,
        title: 'Download Failed',
        message: 'Failed to download CSV report. Please try again.',
        type: 'error'
      });
    }
  };

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value
    });
  };

  const handleApplyFilters = () => {
    fetchReportData();
  };

  const reportTypes = [
    ...(isAdmin ? [{
      id: 'by-owner',
      name: 'By Owner',
      icon: Building2,
      color: 'from-indigo-500 to-indigo-600',
      bgColor: 'bg-indigo-50',
      textColor: 'text-indigo-600',
      description: 'Compare all owners'
    }] : []),
    {
      id: 'bookings',
      name: 'Bookings',
      icon: FileText,
      color: 'from-blue-500 to-blue-600',
      bgColor: 'bg-blue-50',
      textColor: 'text-blue-600',
      description: 'Track all booking activities'
    },
    {
      id: 'revenue',
      name: 'Revenue',
      icon: IndianRupee,
      color: 'from-green-500 to-green-600',
      bgColor: 'bg-green-50',
      textColor: 'text-green-600',
      description: 'Analyze revenue trends'
    },
    {
      id: 'car-utilization',
      name: 'Utilization',
      icon: Car,
      color: 'from-purple-500 to-purple-600',
      bgColor: 'bg-purple-50',
      textColor: 'text-purple-600',
      description: 'Monitor fleet performance'
    },
    {
      id: 'customers',
      name: 'Customers',
      icon: Users,
      color: 'from-orange-500 to-orange-600',
      bgColor: 'bg-orange-50',
      textColor: 'text-orange-600',
      description: 'Customer insights'
    },
    {
      id: 'inspections',
      name: 'Inspections',
      icon: ClipboardCheck,
      color: 'from-pink-500 to-pink-600',
      bgColor: 'bg-pink-50',
      textColor: 'text-pink-600',
      description: 'Condition tracking'
    }
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Reports & Analytics"
        description="Generate comprehensive business insights"
        action={
          <div className="flex gap-2">
            <RefreshButton onClick={() => fetchReportData()} loading={loading} />
            <Button
              variant="primary"
              size="sm"
              onClick={handleDownloadCSV}
              disabled={!reportData || loading}
            >
              <Download className="h-4 w-4 sm:mr-2" />
              <span className="hidden sm:inline">Download CSV</span>
            </Button>
          </div>
        }
      />

      {/* Report Type Tabs */}
      <div className="flex flex-wrap gap-2 mb-3">
        {reportTypes.map((report) => {
          const Icon = report.icon;
          const isActive = activeReport === report.id;
          return (
            <button
              key={report.id}
              onClick={() => setActiveReport(report.id)}
              className={`flex items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors ${
                isActive
                  ? 'bg-blue-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-50 border border-gray-200'
              }`}
            >
              <Icon className="h-4 w-4" />
              <span className="font-medium">{report.name}</span>
            </button>
          );
        })}
      </div>

      {/* Filters Section - Compact */}
      <div className="bg-white rounded-lg shadow-sm p-2 border border-gray-200 mb-3">
        <div className="flex flex-wrap items-center gap-2">
          {/* Date Range - Clear Labels */}
          <div className="flex items-center gap-2">
            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5 border border-gray-200">
              <span className="text-xs font-medium text-gray-500">From</span>
              <input
                type="date"
                name="startDate"
                value={filters.startDate}
                onChange={handleFilterChange}
                className="text-xs h-6 px-1 border-0 bg-transparent focus:ring-0 w-28"
              />
            </div>
            <div className="flex items-center gap-1.5 bg-gray-50 rounded-lg px-2 py-1.5 border border-gray-200">
              <span className="text-xs font-medium text-gray-500">To</span>
              <input
                type="date"
                name="endDate"
                value={filters.endDate}
                onChange={handleFilterChange}
                className="text-xs h-6 px-1 border-0 bg-transparent focus:ring-0 w-28"
              />
            </div>
          </div>

          {/* Status Filter - Bookings */}
          {activeReport === 'bookings' && (
            <select
              name="status"
              value={filters.status}
              onChange={handleFilterChange}
              className="text-xs h-8 px-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="">All Status</option>
              <option value="pending">Pending</option>
              <option value="confirmed">Confirmed</option>
              <option value="active">Active</option>
              <option value="completed">Completed</option>
              <option value="cancelled">Cancelled</option>
            </select>
          )}

          {/* Group By - Revenue */}
          {activeReport === 'revenue' && (
            <select
              name="groupBy"
              value={filters.groupBy}
              onChange={handleFilterChange}
              className="text-xs h-8 px-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white"
            >
              <option value="day">By Day</option>
              <option value="week">By Week</option>
              <option value="month">By Month</option>
              <option value="year">By Year</option>
            </select>
          )}

          {/* Owner Filter - Admin only */}
          {isAdmin && activeReport !== 'by-owner' && (
            <select
              name="ownerId"
              value={filters.ownerId}
              onChange={handleFilterChange}
              className="text-xs h-8 px-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white max-w-[180px]"
            >
              <option value="">All Owners</option>
              {owners.map(owner => (
                <option key={owner._id} value={owner._id}>
                  {formatName(owner.name)} {owner.travelsName ? `(${owner.travelsName})` : ''}
                </option>
              ))}
            </select>
          )}

          {/* Car Filter */}
          {(activeReport === 'bookings' || activeReport === 'inspections') && (
            <select
              name="carId"
              value={filters.carId}
              onChange={handleFilterChange}
              className="text-xs h-8 px-2 border border-gray-200 rounded-lg focus:ring-1 focus:ring-blue-500 bg-white max-w-[150px]"
            >
              <option value="">All Cars</option>
              {cars.map(car => (
                <option key={car._id} value={car._id}>
                  {formatVehicleName(car, { showYear: false })}
                </option>
              ))}
            </select>
          )}

          {/* Apply Button */}
          <button
            onClick={handleApplyFilters}
            className="h-8 px-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors text-xs font-medium"
          >
            Apply
          </button>

          {/* Reset */}
          <button
            onClick={() => {
              const newDates = getDefaultDateRange();
              setFilters({
                ...filters,
                startDate: newDates.start,
                endDate: newDates.end,
                status: '',
                carId: '',
                ownerId: ''
              });
            }}
            className="text-xs text-gray-500 hover:text-gray-700 px-2 py-1.5 hover:bg-gray-100 rounded"
          >
            Reset
          </button>
        </div>
      </div>

      {/* Report Data Display */}
      <div className="bg-white rounded-lg shadow-sm p-3 border border-gray-200">
        {loading ? (
          <div className="py-8">
            <CarLoader message="Loading report data..." />
          </div>
        ) : reportData ? (
          <div>
            {/* Summary Section */}
            {reportData.summary && (
              <div className="mb-4">
                <h3 className="text-sm font-medium text-gray-900 mb-3 flex items-center gap-2">
                  <TrendingUp className="h-4 w-4 text-blue-600" />
                  Summary
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {Object.entries(reportData.summary)
                    .filter(([key, value]) => typeof value !== 'object' || value === null)
                    .map(([key, value]) => {
                    const isMoney = key.toLowerCase().includes('revenue') || key.toLowerCase().includes('value') || key.toLowerCase().includes('spent');
                    return (
                      <div key={key} className="bg-gray-50 rounded-lg p-3 border border-gray-100">
                        <p className="text-xs font-medium text-gray-500 capitalize mb-1">
                          {key.replace(/([A-Z])/g, ' $1').trim()}
                        </p>
                        <p className={`text-lg font-bold ${isMoney ? 'text-green-600' : 'text-gray-900'}`}>
                          {typeof value === 'number' && isMoney
                            ? `₹${value.toLocaleString('en-IN', { maximumFractionDigits: 0 })}`
                            : typeof value === 'number'
                            ? value.toLocaleString('en-IN')
                            : String(value)}
                        </p>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {/* Bookings Table */}
            {activeReport === 'bookings' && reportData.bookings && (
              <div className="overflow-x-auto rounded-lg border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Car</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Customer</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Dates</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                      <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Cost</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.bookings.map((booking) => (
                      <tr key={booking._id} className="hover:bg-blue-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{formatVehicleName(booking.carId, { showYear: false })}</div>
                          <div className="text-sm text-gray-500">{booking.carId?.registrationNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{booking.customerId?.name}</div>
                          <div className="text-sm text-gray-500">{booking.customerId?.email}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm">
                          <div>{new Date(booking.startDate).toLocaleDateString()}</div>
                          <div className="text-gray-500">to {new Date(booking.endDate).toLocaleDateString()}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 text-xs font-semibold rounded-full ${
                            booking.status === 'completed' ? 'bg-green-100 text-green-800' :
                            booking.status === 'active' ? 'bg-blue-100 text-blue-800' :
                            booking.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                            'bg-yellow-100 text-yellow-800'
                          }`}>
                            {booking.status}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-bold text-green-600">
                          ₹{booking.totalCost?.toLocaleString('en-IN') || '0'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Revenue Table */}
            {activeReport === 'revenue' && reportData.data && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-green-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Period</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Revenue</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Bookings</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Avg Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.data.map((item, index) => (
                      <tr key={index} className="hover:bg-green-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{item._id}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-green-600">₹{item.totalRevenue?.toFixed(2) || '0.00'}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-gray-900">{item.bookingCount || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-blue-600">₹{item.averageRevenue?.toFixed(2) || '0.00'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Car Utilization Table */}
            {activeReport === 'car-utilization' && reportData.data && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-purple-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Car</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Bookings</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Revenue</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Days Rented</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Avg/Booking</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.data.map((car) => (
                      <tr key={car.carId} className="hover:bg-purple-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{formatVehicleName(car, { showYear: false })}</div>
                          <div className="text-sm text-gray-500">{car.registrationNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{car.totalBookings}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-green-600">₹{car.totalRevenue?.toLocaleString('en-IN') || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-medium text-blue-600">{Math.round(car.totalDays)} days</td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-purple-600">₹{car.averageRevenuePerBooking?.toLocaleString('en-IN') || '0'}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Customer Table */}
            {activeReport === 'customers' && reportData.data && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-orange-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Customer</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Contact</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Bookings</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Total Spent</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Avg Value</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Last Booking</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.data.map((customer) => (
                      <tr key={customer.customerId} className="hover:bg-orange-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-gray-900">{customer.name}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="text-sm text-gray-900">{customer.email}</div>
                          <div className="text-sm text-gray-500">{customer.phone}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-blue-600">{customer.totalBookings}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-lg font-bold text-green-600">₹{customer.totalSpent?.toLocaleString('en-IN') || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-orange-600">₹{customer.averageBookingValue?.toLocaleString('en-IN') || '0'}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(customer.lastBookingDate).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Inspections Table */}
            {activeReport === 'inspections' && reportData.inspections && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-pink-50">
                    <tr>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Car</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Type</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Date</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Condition</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Rating</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Damages</th>
                      <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase">Repair</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.inspections.map((inspection) => (
                      <tr key={inspection._id} className="hover:bg-pink-50 transition-colors duration-200">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{formatVehicleName(inspection.carId, { showYear: false })}</div>
                          <div className="text-sm text-gray-500">{inspection.carId?.registrationNumber}</div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">{inspection.inspectionType}</td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-600">
                          {new Date(inspection.inspectionDate).toLocaleDateString()}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className="px-3 py-1 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">
                            {inspection.overallCondition}
                          </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap font-bold text-yellow-600">{inspection.overallRating}/5</td>
                        <td className="px-6 py-4 whitespace-nowrap font-semibold text-red-600">{inspection.damages?.length || 0}</td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                            inspection.requiresRepair ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'
                          }`}>
                            {inspection.requiresRepair ? 'Yes' : 'No'}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* By Owner Comparison Table */}
            {activeReport === 'by-owner' && reportData.data && (
              <div className="overflow-x-auto rounded-xl border border-gray-200">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gradient-to-r from-gray-50 to-indigo-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Owner</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-gray-700 uppercase">Travels</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Cars</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Bookings</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Completed</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Active</th>
                      <th className="px-4 py-3 text-center text-xs font-bold text-gray-700 uppercase">Cancelled</th>
                      <th className="px-4 py-3 text-right text-xs font-bold text-gray-700 uppercase">Revenue</th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {reportData.data.map((owner) => (
                      <tr key={owner.ownerId} className="hover:bg-indigo-50 transition-colors duration-200">
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-semibold text-gray-900">{formatName(owner.ownerName)}</div>
                          <div className="text-xs text-gray-500">{owner.ownerPhone}</div>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap">
                          <div className="font-medium text-gray-900">{owner.travelsName || '-'}</div>
                          {owner.travelsCity && (
                            <div className="text-xs text-gray-500">
                              {owner.travelsCity}{owner.travelsState ? `, ${owner.travelsState}` : ''}
                            </div>
                          )}
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center font-semibold text-gray-900">{owner.totalCars}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center font-semibold text-blue-600">{owner.totalBookings}</td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-green-100 text-green-800">{owner.completedBookings}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-blue-100 text-blue-800">{owner.activeBookings}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-center">
                          <span className="px-2 py-0.5 text-xs font-semibold rounded-full bg-red-100 text-red-800">{owner.cancelledBookings}</span>
                        </td>
                        <td className="px-4 py-3 whitespace-nowrap text-right text-lg font-bold text-green-600">
                          ₹{owner.totalRevenue?.toLocaleString('en-IN') || '0'}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  {/* Totals Row */}
                  {reportData.summary && (
                    <tfoot className="bg-gray-50 border-t-2 border-gray-300">
                      <tr>
                        <td className="px-4 py-3 font-bold text-gray-900" colSpan="2">
                          Total ({reportData.summary.totalOwners} owners)
                        </td>
                        <td className="px-4 py-3 text-center font-bold text-gray-900">{reportData.summary.totalCars}</td>
                        <td className="px-4 py-3 text-center font-bold text-blue-700">{reportData.summary.totalBookings}</td>
                        <td className="px-4 py-3" colSpan="3"></td>
                        <td className="px-4 py-3 text-right text-lg font-bold text-green-700">
                          ₹{reportData.summary.totalRevenue?.toLocaleString('en-IN') || '0'}
                        </td>
                      </tr>
                    </tfoot>
                  )}
                </table>
              </div>
            )}

            {reportData.count !== undefined && (
              <div className="mt-6 text-center">
                <p className="text-sm font-semibold text-gray-600">
                  Total Records: <span className="text-blue-600 text-lg">{reportData.count}</span>
                </p>
              </div>
            )}
          </div>
        ) : (
          <div className="text-center py-20">
            <div className="mb-6">
              <TrendingUp className="h-20 w-20 mx-auto text-gray-300" />
            </div>
            <h3 className="text-xl font-bold text-gray-700 mb-2">No Data Available</h3>
            <p className="text-gray-500">Select a report type and apply filters to view insights</p>
          </div>
        )}
      </div>

      <ToastModal
        isOpen={toast.isOpen}
        onClose={() => setToast({ ...toast, isOpen: false })}
        title={toast.title}
        message={toast.message}
        type={toast.type}
      />
    </div>
  );
};

export default Reports;
